/**
 * SettingsPayments — Iter 191k · Multi-provider payments hub.
 *
 * Vendors connect their OWN payment accounts (not platform-shared) so 100%
 * of the sale + payout goes directly to their bank / wallet. Supported:
 *
 *   • Stripe Connect        — existing full onboarding flow (Apple Pay auto)
 *   • PayPal / Venmo        — vendor pastes their PayPal Business REST creds
 *   • Square                — vendor pastes App ID + Access Token + Location
 *   • Interac e-Transfer    — Canada-only: vendor's auto-deposit email
 *   • Apple Pay             — via Stripe (domain registered on Connect acct)
 *
 * Persistence: encrypted secrets stored in `payment_providers` collection
 * keyed by `user_id` via `PUT /api/builder/payment-providers`. GET returns
 * connection status + masked previews only (never raw secrets).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Loader2, ExternalLink,
  Wallet, Apple, CreditCard,
  Eye, EyeOff, Check, Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { DashboardBackdrop, GlassCard } from '@/components/dashboard/dashboard-ui';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ============================== Stripe card ============================== */
function StripeConnectCard({ onConnectedChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/builder/connect/status`, { headers: authHeaders() });
      setStatus(data);
      onConnectedChange?.(data?.charges_enabled);
    } catch { setStatus({ connected: false }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startOnboard = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/builder/connect/onboard`, { origin: window.location.origin }, { headers: authHeaders() });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start onboarding');
      setLoading(false);
    }
  };
  const dashboardLink = async () => {
    try {
      const { data } = await axios.post(`${API}/builder/connect/dashboard-link`, {}, { headers: authHeaders() });
      window.open(data.url, '_blank');
    } catch { toast.error('Could not open Stripe dashboard'); }
  };

  const ready = status?.charges_enabled;
  return (
    <GlassCard className="p-6" data-testid="stripe-connect-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/20 border border-white/10 flex items-center justify-center">
          <Wallet className="w-6 h-6 text-violet-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white">Stripe (cards + Apple Pay + Google Pay)</h3>
              <p className="text-sm text-white/60 mt-1">
                Recommended. Deposits to your bank; supports 135+ currencies. Apple Pay & Google Pay activate
                automatically once your storefront domain is registered.
              </p>
            </div>
            <ConnectPill connected={ready} label={ready ? 'Ready to accept' : status?.connected ? 'Onboarding pending' : 'Not connected'} />
          </div>

          {!status ? (
            <div className="mt-4 text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : !status.connected ? (
            <button
              onClick={startOnboard} disabled={loading} data-testid="stripe-connect-start"
              className="mt-5 px-5 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Connect Stripe account
            </button>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap gap-4 text-sm">
                <Stat label="Account" value={status.stripe_account_id?.slice(0, 16) + '…'} />
                <Stat label="Charges" value={status.charges_enabled ? 'Enabled' : 'Pending'} ok={status.charges_enabled} />
                <Stat label="Payouts" value={status.payouts_enabled ? 'Enabled' : 'Pending'} ok={status.payouts_enabled} />
              </div>
              {!ready && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Finish onboarding to start accepting payments. {status.requirements_currently_due?.length ? `Stripe still needs: ${status.requirements_currently_due.slice(0, 3).join(', ')}.` : ''}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button onClick={startOnboard} data-testid="stripe-resume-onboard" className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm">
                  {ready ? 'Update info' : 'Resume onboarding'}
                </button>
                {ready && (
                  <button onClick={dashboardLink} data-testid="stripe-open-dashboard" className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-2">
                    Open Stripe dashboard <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

const Stat = ({ label, value, ok }) => (
  <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
    <div className="text-[10px] uppercase tracking-wider text-white/40">{label}</div>
    <div className={`text-sm font-medium ${ok === false ? 'text-amber-300' : ok ? 'text-emerald-300' : 'text-white'}`}>{value}</div>
  </div>
);

function ConnectPill({ connected, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${
        connected
          ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25'
          : 'bg-white/5 text-white/60 border-white/15'
      }`}
    >
      {connected ? <Check size={10} /> : null}
      {label}
    </span>
  );
}

/* ============================ Secret field ============================ */
function SecretField({ label, value, onChange, placeholder, testid, hasSaved }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div>
      <div className="text-xs text-white/50 mb-1.5 flex items-center justify-between">
        <span>{label}</span>
        {hasSaved && <span className="text-emerald-400 flex items-center gap-1"><Check size={10} /> Saved</span>}
      </div>
      <div className="relative">
        <input
          type={reveal ? 'text' : 'password'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full px-3 py-2 pr-10 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 outline-none focus:border-violet-400 text-sm font-mono"
        />
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center text-white/45 hover:text-white"
          tabIndex={-1}
        >
          {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      </div>
    </div>
  );
}

const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <div className="text-xs text-white/50 mb-1.5">{label}</div>
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 outline-none focus:border-violet-400 text-sm"
    />
  </div>
);

/* ============================== PayPal card ============================== */
function PayPalCard({ config, onSave }) {
  const [state, setState] = useState({
    enabled: false, sandbox: true, client_id: '', client_secret: '', webhook_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setState((s) => ({ ...s, ...(config || {}) })); }, [config]);

  const change = (patch) => { setState((s) => ({ ...s, ...patch })); setDirty(true); };

  const save = async () => {
    if (!state.client_id || (!state.client_secret && !config?.has_client_secret)) {
      toast.error('Client ID and Secret are required to connect PayPal.');
      return;
    }
    setSaving(true);
    try {
      await onSave('paypal', state);
      toast.success('PayPal settings saved');
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!(await themedConfirm('Disconnect PayPal? Buyers will no longer be able to pay via PayPal or Venmo.'))) return;
    await onSave('paypal', { enabled: false, client_id: '', client_secret: '', webhook_id: '' });
    setState({ enabled: false, sandbox: true, client_id: '', client_secret: '', webhook_id: '' });
    toast.success('PayPal disconnected');
  };

  const connected = state.enabled && !!(config?.has_client_secret || state.client_secret);

  return (
    <GlassCard className="p-6" data-testid="paypal-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#003087]/40 to-[#0070BA]/30 border border-white/10 flex items-center justify-center">
          <span className="text-[13px] font-black text-white tracking-tight">Pay<span className="text-[#00B7FF]">Pal</span></span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white">PayPal &amp; Venmo</h3>
              <p className="text-sm text-white/60 mt-1">
                Connect your PayPal Business account so buyers can pay with PayPal or Venmo. Funds land in your PayPal wallet
                — <span className="text-white">EntrepreNatives never touches them</span>.
              </p>
            </div>
            <ConnectPill connected={connected} label={connected ? 'Connected' : 'Not connected'} />
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
            <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-white/75 leading-snug">
              Get your credentials at{' '}
              <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="underline text-[#A78BFA] hover:text-white">
                developer.paypal.com → Apps &amp; Credentials
              </a>. Create a REST app under your live Business account.
            </p>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <TextField
              label="Client ID"
              value={state.client_id}
              onChange={(v) => change({ client_id: v })}
              placeholder="AXbf7…live client id"
              testid="paypal-client-id"
            />
            <SecretField
              label="Secret"
              value={state.client_secret}
              onChange={(v) => change({ client_secret: v })}
              placeholder={config?.has_client_secret ? '•••••••• (saved)' : 'EL7uSJ…'}
              testid="paypal-secret"
              hasSaved={!!config?.has_client_secret}
            />
            <TextField
              label="Webhook ID (optional, for verified events)"
              value={state.webhook_id}
              onChange={(v) => change({ webhook_id: v })}
              placeholder="1JE3…"
              testid="paypal-webhook-id"
            />
            <div>
              <div className="text-xs text-white/50 mb-1.5">Mode</div>
              <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
                {['sandbox', 'live'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => change({ sandbox: m === 'sandbox' })}
                    className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wider ${
                      (state.sandbox && m === 'sandbox') || (!state.sandbox && m === 'live')
                        ? 'bg-[#A78BFA] text-[#0E0E23]'
                        : 'bg-transparent text-white/60 hover:bg-white/5'
                    }`}
                    data-testid={`paypal-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <label className="flex items-center gap-2 text-[12px] text-white/70">
              <input
                type="checkbox"
                checked={state.enabled}
                onChange={(e) => change({ enabled: e.target.checked })}
                data-testid="paypal-enabled"
              />
              Enable at checkout
            </label>
            <div className="flex-1" />
            {connected && (
              <button
                onClick={disconnect}
                className="px-3 py-2 rounded-lg text-[11px] font-semibold text-rose-300 bg-rose-500/10 hover:bg-rose-500/15"
                data-testid="paypal-disconnect"
              >
                Disconnect
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`px-4 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
                dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
              }`}
              data-testid="paypal-save"
            >
              {saving ? 'Saving…' : 'Save PayPal'}
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

/* ============================== Square card ============================== */
function SquareCard({ config, onSave }) {
  const [state, setState] = useState({
    enabled: false, sandbox: true, application_id: '', access_token: '', location_id: '', webhook_signature_key: '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setState((s) => ({ ...s, ...(config || {}) })); }, [config]);

  const change = (patch) => { setState((s) => ({ ...s, ...patch })); setDirty(true); };
  const save = async () => {
    if (!state.application_id || !state.location_id || (!state.access_token && !config?.has_access_token)) {
      toast.error('Application ID, Access Token, and Location ID are all required.');
      return;
    }
    setSaving(true);
    try {
      await onSave('square', state);
      toast.success('Square settings saved');
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!(await themedConfirm('Disconnect Square?'))) return;
    await onSave('square', { enabled: false, application_id: '', access_token: '', location_id: '' });
    setState({ enabled: false, sandbox: true, application_id: '', access_token: '', location_id: '', webhook_signature_key: '' });
    toast.success('Square disconnected');
  };

  const connected = state.enabled && !!(config?.has_access_token || state.access_token) && !!state.location_id;

  return (
    <GlassCard className="p-6" data-testid="square-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#000]/50 to-[#333]/40 border border-white/10 flex items-center justify-center">
          <span className="w-5 h-5 rounded-sm bg-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white">Square</h3>
              <p className="text-sm text-white/60 mt-1">
                Great for vendors who already sell in-person via Square. Funds settle into your Square balance.
                Availability: US, Canada, UK, Australia, Japan, Ireland, France, Spain.
              </p>
            </div>
            <ConnectPill connected={connected} label={connected ? 'Connected' : 'Not connected'} />
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
            <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-white/75 leading-snug">
              Get your credentials at{' '}
              <a href="https://developer.squareup.com/apps" target="_blank" rel="noreferrer" className="underline text-[#A78BFA] hover:text-white">
                developer.squareup.com → Apps
              </a>. Grab the <b>Application ID</b>, an <b>Access Token</b> (Production or Sandbox), and your <b>Location ID</b>.
            </p>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <TextField
              label="Application ID"
              value={state.application_id}
              onChange={(v) => change({ application_id: v })}
              placeholder="sq0idp-abc123…"
              testid="square-app-id"
            />
            <SecretField
              label="Access Token"
              value={state.access_token}
              onChange={(v) => change({ access_token: v })}
              placeholder={config?.has_access_token ? '•••••••• (saved)' : 'EAAAE…'}
              testid="square-access-token"
              hasSaved={!!config?.has_access_token}
            />
            <TextField
              label="Location ID"
              value={state.location_id}
              onChange={(v) => change({ location_id: v })}
              placeholder="L1AB2CD3E4F5G"
              testid="square-location-id"
            />
            <SecretField
              label="Webhook signature key (optional)"
              value={state.webhook_signature_key}
              onChange={(v) => change({ webhook_signature_key: v })}
              placeholder={config?.has_webhook_signature_key ? '•••••••• (saved)' : 'For signed webhook verification'}
              testid="square-webhook-key"
              hasSaved={!!config?.has_webhook_signature_key}
            />
            <div>
              <div className="text-xs text-white/50 mb-1.5">Mode</div>
              <div className="inline-flex rounded-md border border-white/10 overflow-hidden">
                {['sandbox', 'live'].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => change({ sandbox: m === 'sandbox' })}
                    className={`px-4 py-2 text-[11px] font-semibold uppercase tracking-wider ${
                      (state.sandbox && m === 'sandbox') || (!state.sandbox && m === 'live')
                        ? 'bg-[#A78BFA] text-[#0E0E23]'
                        : 'bg-transparent text-white/60 hover:bg-white/5'
                    }`}
                    data-testid={`square-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <label className="flex items-center gap-2 text-[12px] text-white/70">
              <input
                type="checkbox"
                checked={state.enabled}
                onChange={(e) => change({ enabled: e.target.checked })}
                data-testid="square-enabled"
              />
              Enable at checkout
            </label>
            <div className="flex-1" />
            {connected && (
              <button
                onClick={disconnect}
                className="px-3 py-2 rounded-lg text-[11px] font-semibold text-rose-300 bg-rose-500/10 hover:bg-rose-500/15"
                data-testid="square-disconnect"
              >
                Disconnect
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`px-4 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
                dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
              }`}
              data-testid="square-save"
            >
              {saving ? 'Saving…' : 'Save Square'}
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

/* =========================== E-Transfer card =========================== */
function ETransferCard({ config, onSave }) {
  const [state, setState] = useState({
    enabled: false, email: '', display_name: '', security_question: '', security_answer: '', auto_deposit: true,
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setState((s) => ({ ...s, ...(config || {}) })); }, [config]);

  const change = (patch) => { setState((s) => ({ ...s, ...patch })); setDirty(true); };
  const save = async () => {
    if (!state.email || !state.email.includes('@')) {
      toast.error('Auto-deposit email is required.');
      return;
    }
    if (!state.auto_deposit && (!state.security_question || !state.security_answer)) {
      toast.error('Security question and answer are required if Auto-deposit is off.');
      return;
    }
    setSaving(true);
    try {
      await onSave('etransfer', state);
      toast.success('e-Transfer settings saved');
      setDirty(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const connected = state.enabled && !!state.email;

  return (
    <GlassCard className="p-6" data-testid="etransfer-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/30 to-teal-500/20 border border-white/10 flex items-center justify-center">
          <span className="text-[13px] font-black text-white">e</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white">Interac e-Transfer <span className="text-[10px] uppercase tracking-wider text-emerald-300 ml-2">Canada</span></h3>
              <p className="text-sm text-white/60 mt-1">
                For Canadian vendors. Buyers send Interac e-Transfer from their online banking to your registered
                auto-deposit email — funds land directly in your bank, no fees.
              </p>
            </div>
            <ConnectPill connected={connected} label={connected ? 'Connected' : 'Not connected'} />
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
            <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-white/75 leading-snug">
              Register this email for Auto-Deposit in your Canadian bank first (RBC, TD, Scotiabank, BMO, CIBC, etc.).
              We&apos;ll show buyers the email + a unique reference code at checkout, and mark orders as paid once you confirm receipt.
            </p>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <TextField
              label="Auto-deposit email"
              value={state.email}
              onChange={(v) => change({ email: v.trim().toLowerCase() })}
              placeholder="you@yourbusiness.com"
              testid="etransfer-email"
            />
            <TextField
              label="Display name (shown to buyer)"
              value={state.display_name}
              onChange={(v) => change({ display_name: v })}
              placeholder="Sarah's Studio"
              testid="etransfer-display-name"
            />
          </div>

          <div className="mt-3 flex items-center gap-2 text-[12px] text-white/70">
            <input
              type="checkbox"
              checked={state.auto_deposit}
              onChange={(e) => change({ auto_deposit: e.target.checked })}
              data-testid="etransfer-auto-deposit"
            />
            My email is registered for Auto-Deposit (recommended — no security question needed)
          </div>

          {!state.auto_deposit && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <TextField
                label="Security question"
                value={state.security_question}
                onChange={(v) => change({ security_question: v })}
                placeholder="What was our order ID?"
                testid="etransfer-question"
              />
              <TextField
                label="Answer (buyer must type this)"
                value={state.security_answer}
                onChange={(v) => change({ security_answer: v })}
                placeholder="Order number"
                testid="etransfer-answer"
              />
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <label className="flex items-center gap-2 text-[12px] text-white/70">
              <input
                type="checkbox"
                checked={state.enabled}
                onChange={(e) => change({ enabled: e.target.checked })}
                data-testid="etransfer-enabled"
              />
              Enable at checkout
            </label>
            <div className="flex-1" />
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`px-4 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider ${
                dirty && !saving ? 'bg-[#A78BFA] text-[#0E0E23] hover:bg-white' : 'bg-white/5 text-white/40 cursor-not-allowed'
              }`}
              data-testid="etransfer-save"
            >
              {saving ? 'Saving…' : 'Save e-Transfer'}
            </button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

/* ============================ Apple Pay card ============================ */
function ApplePayCard({ stripeReady }) {
  const [status, setStatus] = useState(null);
  const [domain, setDomain] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/builder/apple-pay/status`, { headers: authHeaders() });
      setStatus(data);
      setDomain(data?.domain || '');
    } catch { setStatus({ registered: false }); }
  };
  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!domain.trim()) { toast.error('Enter your storefront domain first.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/builder/apple-pay/register-domain`, { domain: domain.trim() }, { headers: authHeaders() });
      setStatus(data);
      toast.success('Apple Pay domain registered — the button will appear on your storefront.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not register domain'); }
    finally { setBusy(false); }
  };

  const connected = !!status?.registered;

  return (
    <GlassCard className="p-6" data-testid="apple-pay-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-black/60 to-zinc-800/40 border border-white/10 flex items-center justify-center">
          <Apple className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white">Apple Pay</h3>
              <p className="text-sm text-white/60 mt-1">
                One-tap checkout on Safari (iPhone, iPad, Mac). Runs through your Stripe account —
                so you get the same payout schedule as regular card sales.
              </p>
            </div>
            <ConnectPill connected={connected} label={connected ? 'Domain verified' : stripeReady ? 'Ready to register' : 'Connect Stripe first'} />
          </div>

          {!stripeReady ? (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-amber-200 text-[12px]">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              Apple Pay uses your Stripe account. Connect Stripe above first, then come back to register your storefront domain.
            </div>
          ) : (
            <>
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#A78BFA]/8 border border-[#A78BFA]/20 p-3">
                <Info size={13} className="text-[#A78BFA] mt-0.5 shrink-0" />
                <p className="text-[11.5px] text-white/75 leading-snug">
                  Enter the exact domain buyers use to visit your store (e.g. <code className="font-mono text-white/90">shop.yourbrand.com</code>).
                  We register it with Stripe; Apple validates automatically. HTTPS required.
                </p>
              </div>

              <div className="mt-4 flex gap-2">
                <input
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="shop.yourbrand.com"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 outline-none focus:border-violet-400 text-sm font-mono"
                  data-testid="apple-pay-domain-input"
                />
                <button
                  onClick={register}
                  disabled={busy || !domain.trim()}
                  className="px-4 py-2 rounded-lg bg-[#A78BFA] hover:bg-white text-[#0E0E23] text-sm font-bold uppercase tracking-wider disabled:opacity-50 flex items-center gap-2"
                  data-testid="apple-pay-register"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldIcon />}
                  {connected ? 'Re-verify' : 'Register'}
                </button>
              </div>

              {status?.registered_at && (
                <p className="mt-2 text-[11px] text-white/45">
                  Verified {new Date(status.registered_at).toLocaleDateString()} · Status: <span className="text-emerald-300">{status.state || 'active'}</span>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

const ShieldIcon = () => <CreditCard size={13} />;

/* ================================= Page ================================= */
export default function SettingsPayments() {
  const [config, setConfig] = useState({ paypal: {}, square: {}, etransfer: {}, apple_pay: {} });
  const [loading, setLoading] = useState(true);
  const [stripeReady, setStripeReady] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/builder/payment-providers`, { headers: authHeaders() });
      setConfig((c) => ({ ...c, ...(data || {}) }));
    } catch { /* first visit */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePart = async (key, payload) => {
    const { data } = await axios.put(`${API}/builder/payment-providers`, { [key]: payload }, { headers: authHeaders() });
    setConfig((c) => ({ ...c, ...(data || {}) }));
  };

  return (
    <div className="relative min-h-full">
      <DashboardBackdrop />
      <div className="relative z-10 max-w-4xl mx-auto px-6 py-10 space-y-6" data-testid="settings-payments">
        <Link to="/website-builder/settings" data-testid="settings-go-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white hover:text-[#A78BFA] transition-colors font-semibold">
          <ArrowLeft className="w-3 h-3" /> Go Back
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Payments</h1>
          <p className="text-white/60 mt-2">
            Connect any combination of payment providers you like — <span className="text-white">100% of every sale goes to you</span>,
            EntrepreNatives never takes a cut. Buyers see every connected provider as an option at checkout.
          </p>
        </div>

        {loading ? (
          <GlassCard className="p-8 text-white/50 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading providers…
          </GlassCard>
        ) : (
          <>
            <StripeConnectCard onConnectedChange={setStripeReady} />
            <ApplePayCard stripeReady={stripeReady} />
            <PayPalCard    config={config.paypal}    onSave={savePart} />
            <SquareCard    config={config.square}    onSave={savePart} />
            <ETransferCard config={config.etransfer} onSave={savePart} />
          </>
        )}
      </div>
    </div>
  );
}
